import { FC } from 'react';
import { ThemeProvider } from 'styled-components';
import {
  CommonContextProvider,
  AuthContextProvider,
  GamesContextProvider,
  PrimarySettingsProvider,
  composeProviders,
  useCreateNoscript,
  useCreateScript,
} from '@repo-level/core-front';

import { Theme } from 'theme/contracts';
import { GlobalStyles } from 'components/styles/GlobalStyles';
import { ErrorBoundary } from 'components/internal/ErrorBoundary';
import { InternalError } from 'components/internal/InternalError';
import { Routes } from 'routes';
import { BackgroundContextProvider } from 'contexts/background';
import { ModalsContextProvider } from 'contexts/modal';
import { IntercomContextProvider } from 'contexts/intercom';
import { Restricted } from 'contexts/restricted';
import { gtm } from 'assets/noscripts/gtm';
import { AddonsContextProvider } from 'contexts/addons';
import { HotjarContextProvider } from 'contexts/hotjar';
import { StagContextProvider } from 'contexts/stag';
import { GradientsList } from 'components/styles/GradientsList';
import { CommonUtilsContextProvider } from 'contexts/commonUtils';
import { LobbySliderContextProvider } from 'contexts/slider';
import { TabBarContextProvider } from 'contexts/tabBarSize';
import { BONUS_RESTRICTED_GROUPS, DEFAULT_NEW_USER_GROUPS } from 'helpers/user';
import { BONUS_RESTRICTED_COUNTRIES } from 'helpers/conf';
import { RealTimeServiceContextProvider } from './contexts/realTimeService';
import { OneSignalContextProvider } from 'contexts/oneSignal';
import { NotificationsContextProvider } from 'contexts/notifications';
import { TournamentsContextProvider } from 'contexts/tournaments';
import { BonanzaContextProvider } from 'contexts/bonanza';
import { MobileAppContextProvider } from 'modules/mobileApp/context';
import { StardustRushContextProvider } from 'contexts/stardustRush/stardustRush';
import { ReferralProgramContextProvider } from 'contexts/referralProgram';
import { useServiceWorker } from 'hooks/useServiceWorkerHook';
interface AppProps {
  theme: Theme;
}

const PrimarySettingsContextProvider: FC = ({ children }) => (
  <PrimarySettingsProvider
    defaultValues={{
      defaultUserStatuses: DEFAULT_NEW_USER_GROUPS,
      bonusRestrictedGroups: BONUS_RESTRICTED_GROUPS,
      bonusRestrictedCountries: BONUS_RESTRICTED_COUNTRIES,
    }}
  >
    {children}
  </PrimarySettingsProvider>
);

const Provider = composeProviders([
  PrimarySettingsContextProvider,
  CommonContextProvider,
  AuthContextProvider,
  StagContextProvider,
  OneSignalContextProvider,
  NotificationsContextProvider,
  TournamentsContextProvider,
  GamesContextProvider,
  BackgroundContextProvider,
  ModalsContextProvider,
  IntercomContextProvider,
  CommonUtilsContextProvider,
  AddonsContextProvider,
  HotjarContextProvider,
  LobbySliderContextProvider,
  TabBarContextProvider,
  RealTimeServiceContextProvider,
  Restricted,
  BonanzaContextProvider,
  MobileAppContextProvider,
  StardustRushContextProvider,
  ReferralProgramContextProvider,
]);

const App: FC<AppProps> = ({ theme }) => {
  useCreateScript(
    '/pixels/google-tag-manager.js',
    window.appConfig.APP_ENV !== 'development'
  );

  useServiceWorker();
  useCreateNoscript(gtm, window.appConfig.APP_ENV !== 'development');

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyles />

      <ErrorBoundary fallback={<InternalError />}>
        <Provider>
          <Routes />
        </Provider>
      </ErrorBoundary>
      <GradientsList />
    </ThemeProvider>
  );
};

export default App;
